import Person from '@manomano-internal/tamagoshi-icons/esm/material/Person';
import dynamic from 'next/dynamic';
import { useState, type FC } from 'react';
import { Carousel } from '@/core/tamagoshi/Carousel/Carousel';
import { Text } from '@/core/tamagoshi/Text/Text';
import { Image } from '@/domains/core/Image/Image';
import ReadMore from '@/domains/core/page/components/ReadMore/ReadMore';
import { Rating as TamagoshiRatings } from '@/domains/core/tamagoshiTailwind/components/Rating/Rating';
import { rating as ratingTranslation } from '@/domains/core/tamagoshiTailwind/translations';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import type { Picture, Review } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { ReviewDateTag } from '@/domains/productDiscovery/ArticleReviews/components/ReviewDateTag/ReviewDateTag';
import type { ReviewModalProps } from '@/domains/productDiscovery/ArticleReviews/components/ReviewModal/ReviewModal.types';
import { avisqualiTranslation, incentivizedTranslation } from '@/domains/productDiscovery/ArticleReviews/components/translations';
import { beautifyNickname } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/utils.reviewsBlock';
import { reviewsShowLess, reviewsShowMore } from '@/domains/productDiscovery/ArticleReviews/translations';
import { ReviewCardVoting } from '../ReviewCardVoting/ReviewCardVoting';
import { ReviewPurchaseDate } from '../ReviewPurchaseDate/ReviewPurchaseDate';
import { proCustomer, translatedByGoogle } from './translations';
import styles from './reviewcard.module.scss';
interface propsReviewCard extends Review {
  hideTranslatedTag?: boolean;
  hidePictures?: boolean;
}
const ReviewModal = dynamic<ReviewModalProps>(() => import('@/domains/productDiscovery/ArticleReviews/components/ReviewModal/ReviewModal').then(mod => mod.ReviewModal));
export const ReviewCard: FC<propsReviewCard> = ({
  hideTranslatedTag = false,
  hidePictures = false,
  ...review
}) => {
  const {
    createdAt,
    title,
    rating,
    message,
    isTranslated,
    pictures,
    reviewId,
    votes,
    incentivized,
    purchaseDate,
    source,
    isB2b: reviewIsB2b,
    reviewer
  } = review;
  const [selectedImage, setSelectedImage] = useState<Picture | null>(null);
  const onClickPicture = (pic: Picture) => {
    setSelectedImage(pic);
    Gtm.push({
      event: 'interaction_click on review picture',
      e_category: 'navigation',
      e_action: 'Click on review picture',
      amp_event_name: 'Click on review picture',
      event_trigger: 'review_card',
      interaction_hit: 'False'
    });
  };
  const showPictures = !hidePictures && !!pictures.length;
  const showTranslatedTag = isTranslated && !hideTranslatedTag;
  const incentivizedReviewDisclaimer = incentivized ? ` ${incentivizedTranslation}` : '';
  const testerReviewDisclaimer = source === 'aviquali' ? `[${avisqualiTranslation}] ` : '';
  return <>
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.name}>{beautifyNickname(reviewer)}</div>
          <ReviewDateTag createdAt={createdAt} />
        </header>
        {title && <div className={styles.title}>{title}</div>}
        {reviewIsB2b && <div className={styles.proUser}>
            <Person />
            <span>{proCustomer}</span>
          </div>}

        <TamagoshiRatings aria-label={ratingTranslation.ariaLabel} value={rating} size="S" />
        <ReviewPurchaseDate purchasedAt={purchaseDate} />
        <ReadMore textLess={reviewsShowLess} textMore={reviewsShowMore} textClassName={styles.readMore} className={styles.readMore}>
          {`${testerReviewDisclaimer}${message || '-'}${incentivizedReviewDisclaimer}`}
        </ReadMore>
        {showTranslatedTag && <Text className={styles.translatedTag}>{translatedByGoogle}</Text>}
        {showPictures && <Carousel title="">
            {pictures.map((pic, index) => <Image key={pic.uuid} height={132} width={132} src={pic.thumbnail} alt={`Picture ${index}`} onClick={() => onClickPicture(pic)} className={styles.picture} unoptimized={false} />)}
          </Carousel>}
        <ReviewCardVoting reviewId={reviewId} votes={votes} className={styles.voting} />
      </div>
      {selectedImage && <ReviewModal {...review} selectedImage={selectedImage} onClose={() => setSelectedImage(null)} />}
    </>;
};